import {
  WithBreakpoints,
  Spacings,
  BorderWidths,
  BorderRadiuses,
  ResponsiveTokens,
  FontSizes,
  SizesMap,
  StaticTokens,
  LetterSpacings,
  LineHeights,
} from './index.types';

export const DARK_PALETTE = {
  emerald: {
    100: '#F4FFDD',
    200: '#E3FAB3',
    300: '#BEEF70',
    400: '#6FCE78',
    500: '#09A75D',
    600: '#16804F',
    700: '#14613E',
    800: '#114F33',
  },
  amethyst: {
    100: '#FFF0F9',
    200: '#FFD5FB',
    300: '#F799FF',
    400: '#CD69EE',
    500: '#A022ED',
    600: '#7D18BA',
    700: '#581C95',
    800: '#39165B',
  },
  sapphire: {
    100: '#F6FBFF',
    200: '#C7F5FF',
    300: '#95E3F3',
    400: '#45C4EE',
    500: '#2481ED',
    600: '#236CB4',
    700: '#1554A2',
    800: '#163B63',
  },
  garnet: {
    100: '#FFF4F8',
    200: '#FF9EC1',
    300: '#FF7AAA',
    400: '#FF669D',
    500: '#FF4789',
    600: '#BB2259',
    700: '#991544',
    800: '#650829',
  },
  topaz: {
    100: '#FFF9E5',
    200: '#FFEBA5',
    300: '#FFE27B',
    400: '#FFD747',
    500: '#FFC700',
    600: '#C7841A',
    700: '#9B641C',
    800: '#51310D',
  },
  aquamarine: {
    100: '#ECFFF8',
    200: '#B9F8E8',
    300: '#94F5DC',
    400: '#66F0CD',
    500: '#1DE9B6',
    600: '#26836C',
    700: '#1D6D59',
    800: '#1F493A',
  },
  ruby: {
    100: '#FFECEB',
    200: '#F99790',
    300: '#F7776E',
    400: '#F55C51',
    500: '#F44336',
    600: '#C52417',
    700: '#90140B',
    800: '#5E0E08',
  },
  stone: {
    100: '#FBFBFB',
    200: '#F2F2F2',
    300: '#CECECE',
    400: '#B2B2B2',
    500: '#A0A0A0',
    600: '#898989',
    700: '#6E6E6E',
    800: '#5C5C5C',
  },
  'stone-alpha': {
    100: '#FDFDFDFC',
    200: '#FDFDFDF2',
    300: '#FDFDFDCC',
    400: '#FEFEFEAD',
    500: '#FEFEFE99',
    600: '#FDFDFD80',
    700: '#FCFCFC63',
    800: '#FBFBFB52',
  },
  onyx: {
    100: '#4B4B4B',
    200: '#424242',
    300: '#3A3A3A',
    400: '#2A2A2A',
    500: '#222222',
    600: '#191919',
    700: '#121212',
    800: '#0A0A0A',
  },
  'onyx-alpha': {
    100: '#0000000a',
    200: '#0000002b',
    300: '#0000005c',
    400: '#0000008f',
    500: '#000000c4',
    600: '#000000de',
    700: '#000000e6',
    800: '#000000f7',
  },
  'topaz-alpha': {
    100: '#ffc7001a',
    200: '#ffc7005c',
    300: '#ffc7008a',
    400: '#ffc700b8',
    500: '#ffc700',
    600: '#ffa100c7',
    700: '#ff9a0099',
    800: '#ff89004a',
  },
  neutral: {
    white: '#ffffff',
    black: '#000000',
  },
  gradients: {
    global: {
      goldA:
        'linear-gradient(127deg, #ffeba5 0%, #ffc700 84.61%, #ffe27b 100%)',
      goldB: 'linear-gradient(134deg, #ffc700 13.02%, #ffe27b 77.97%)',
      sunset: 'linear-gradient(226deg, #ff4789 0%, #f5b400 100%)',
      citrus: 'linear-gradient(90deg, #ffc700 0%, #ffe27b 50%, #1de9b6 100%)',
      ember: 'linear-gradient(45deg, #d91a0c 0%, #ffc700 100%)',
      warning: 'linear-gradient(266.48deg, #ff4789 0%, #f28016 100%);',
      emphasize:
        'linear-gradient(45deg, transparent 0%, #fff 40%, #fff 60%, transparent 100%)',
      grayScale:
        'linear-gradient(127deg, #A0A0A0 0%, #898989 84.61%, #6E6E6E 100%)',
      graph: 'linear-gradient(90deg, #000 0%, #fff 46.5%, #fff 100%)',
      graphInversed: 'linear-gradient(90deg, #fff 0%, #fff 46.5%, #000 100%)',
      horizontalOverlay:
        'linear-gradient(90deg, transparent 0%, #000000c4 15%, #000000c4 85%, transparent 100%)',
      secondaryHalfLeftOverlay:
        'linear-gradient(90deg, transparent 0%, #000000f7 50%, #000000f7 100%)',
      fullOverlay:
        'linear-gradient(90deg, transparent 0%, #0000004D 0%, #0000004D 100%)',
      verticalGrayLine:
        'linear-gradient(180deg, transparent 0%, #6E6E6E33 50%, transparent 100%)',
      warmUp:
        'linear-gradient(192.62deg, #DDDDE0 3.99%, #FFC000 35.02%, #D22E00 97.21%)',
      warmedUp: 'linear-gradient(180deg, #FFC000 0%, #D22E00 75%, #6B2A00 93%)',
      purpleVerticalGradient: 'linear-gradient(180deg, rgba(56, 0, 169, 0) 0%, #3800A9 100%), #0F071F',
      verticalBlueGradient: 'linear-gradient(180deg, rgba(56, 0, 169, 0.20) 0%, #3800A9 100%), #121212;',
    },
    level: {
      vip: 'linear-gradient(62deg, #f79fff 1.5%, #0ec5ff 35%, #88f086 69%, #ffea9e 100%)',
      ruby: 'linear-gradient(38deg, #fb4179 0%, #ffa7dc 100%)',
      opal: 'linear-gradient(53deg, #c38cee 0%, #a2d0df 48%, #fcedd5 100%)',
      topaz: 'linear-gradient(40deg, #f67b23 0%, #ffeeb1 100%)',
      member: 'linear-gradient(45deg, #fff 100%, #fff 100%)',
      diamond: 'linear-gradient(38deg, #5286b7 0%, #f0faff 100%)',
      emerald: 'linear-gradient(38deg, #09a75b 0%, #e3fab3 100%)',
      sapphire: 'linear-gradient(62deg, #2481ed 0%, #aef9f5 100%)',
      amethyst: 'linear-gradient(34deg, #A022ED 0%, #ffa3da 66.5%)',
      aquamarine: 'linear-gradient(64deg, #08af91 0%, #cbf9da 100%)',
    },
    attention: 'linear-gradient(45deg, #d32f2f 37.5%, #ff9e2c 100%)',
    citrus: 'linear-gradient(87deg, #FFC400 0%, #FFE168 50%, #00EDB3 100%)',
  },
} as const;

export const LIGHT_PALETTE = DARK_PALETTE;

export const DARK_TOKENS = {
  // Background colors
  screenBackground: DARK_PALETTE.onyx[700],
  primaryBackground: DARK_PALETTE.onyx[600],
  bonanzaNewBackground: '#180A36',
  bonanzaCarouselBackground: '#0F071F',
  overlayBackground: DARK_PALETTE['stone'][600],
  bgOverlayDarkScrimBackground: DARK_PALETTE['onyx-alpha'][500],
  primaryBackgroundHover: DARK_PALETTE['onyx-alpha'][500],
  transparentModalOverlayBackground: DARK_PALETTE['onyx-alpha'][500],
  secondaryBackground: DARK_PALETTE['onyx-alpha'][800],
  secondaryBackgroundHover: DARK_PALETTE['onyx-alpha'][600],
  tertiaryBackgroundHover: DARK_PALETTE['onyx-alpha'][400],
  mutedBackground: DARK_PALETTE.onyx[500],
  accentBackground: DARK_PALETTE.gradients.global.goldA,
  accentBackgroundHover: DARK_PALETTE.gradients.global.goldB,
  whiteBackground: DARK_PALETTE.neutral.white,
  successBackground: DARK_PALETTE.aquamarine[500],
  blackBackground: DARK_PALETTE.neutral.black,
  accentSolidBackground: DARK_PALETTE.topaz[200],
  secondaryBackgroundDefault: DARK_PALETTE['onyx'][500],
  secondaryBackgroundDisabled: DARK_PALETTE['topaz-alpha'][100],
  tertiaryBackgroundDisabled: DARK_PALETTE['topaz-alpha'][200],
  warningBackground: DARK_PALETTE.gradients.global.warning,
  citrusBackground: DARK_PALETTE.gradients.citrus,
  attentionBackground: DARK_PALETTE.gradients.attention,
  washedBackground: DARK_PALETTE['stone-alpha'][800],
  statusMemberBackground: DARK_PALETTE.gradients.level.member,
  statusAmethystBackground: DARK_PALETTE.gradients.level.amethyst,
  statusTopazBackground: DARK_PALETTE.gradients.level.topaz,
  statusAquamarineBackground: DARK_PALETTE.gradients.level.aquamarine,
  statusOpalBackground: DARK_PALETTE.gradients.level.opal,
  statusEmeraldBackground: DARK_PALETTE.gradients.level.emerald,
  statusRubyBackground: DARK_PALETTE.gradients.level.ruby,
  statusSapphireBackground: DARK_PALETTE.gradients.level.sapphire,
  statusDiamondBackground: DARK_PALETTE.gradients.level.diamond,
  statusVipBackground: DARK_PALETTE.gradients.level.vip,
  horizontalOverlayBackground: DARK_PALETTE.gradients.global.horizontalOverlay,
  primarySolidBackgroundPale: DARK_PALETTE.topaz[400],
  tertiaryBackground: DARK_PALETTE['onyx'][200],
  onLightTertiaryBackground: DARK_PALETTE['onyx'][400],
  tertiaryCustomBackground: DARK_PALETTE.stone[200],
  primarySolidBackground: DARK_PALETTE.topaz[500],
  secondaryLeftOverlayBackground:
  DARK_PALETTE.gradients.global.secondaryHalfLeftOverlay,
  fullOverlayBackground: DARK_PALETTE.gradients.global.fullOverlay,
  redBackground: DARK_PALETTE.ruby[800],
  greenBackground: DARK_PALETTE.emerald[500],
  verticalGreyBackground: DARK_PALETTE.gradients.global.verticalGrayLine,
  bonanzaLandingPageBackground: DARK_PALETTE.gradients.global.purpleVerticalGradient,
  whyBonanzaSmallBoxesBackground: DARK_PALETTE.gradients.global.verticalBlueGradient,

  statusAmethystPerksBackground: DARK_PALETTE.amethyst[300],
  statusTopazPerksBackground: DARK_PALETTE.topaz[300],
  statusAquamarinePerksBackground: DARK_PALETTE.aquamarine[300],
  statusOpalPerksBackground: DARK_PALETTE.aquamarine[100],
  statusEmeraldPerksBackground: DARK_PALETTE.emerald[200],
  statusRubyPerksBackground: DARK_PALETTE.ruby[200],
  statusSapphirePerksBackground: DARK_PALETTE.sapphire[200],
  statusDiamondPerksBackground: DARK_PALETTE.sapphire[100],

  // Text colors
  darkSurfacePrimary: DARK_PALETTE.neutral.white,
  darkSurfaceSecondary: DARK_PALETTE.stone[500],
  darkSurfaceTertiary: DARK_PALETTE.stone[200],
  darkSurfaceStone: DARK_PALETTE.stone[300],
  darkSurfaceMuted: DARK_PALETTE.stone[400],
  darkSurfaceWashed: DARK_PALETTE['stone-alpha'][800],
  darkSurfaceDisabled: DARK_PALETTE.stone[700],
  darkSurfaceAccent: DARK_PALETTE.gradients.global.goldA,
  darkSurfaceAccentSolid: DARK_PALETTE.topaz[400],
  darkSurfaceAccentDisabled: DARK_PALETTE['topaz-alpha'][200],
  darkSurfaceBrand: DARK_PALETTE.gradients.global.citrus,
  darkSurfaceSuccess: DARK_PALETTE.aquamarine[500],
  darkSurfaceError: DARK_PALETTE.ruby[500],

  darkSurfaceStatusMember: DARK_PALETTE.gradients.level.member,
  darkSurfaceStatusAmethyst: DARK_PALETTE.gradients.level.amethyst,
  darkSurfaceStatusTopaz: DARK_PALETTE.gradients.level.topaz,
  darkSurfaceStatusAquamarine: DARK_PALETTE.gradients.level.aquamarine,
  darkSurfaceStatusOpal: DARK_PALETTE.gradients.level.opal,
  darkSurfaceStatusEmerald: DARK_PALETTE.gradients.level.emerald,
  darkSurfaceStatusRuby: DARK_PALETTE.gradients.level.ruby,
  darkSurfaceStatusSapphire: DARK_PALETTE.gradients.level.sapphire,
  darkSurfaceStatusDiamond: DARK_PALETTE.gradients.level.diamond,
  darkSurfaceStatusVip: DARK_PALETTE.gradients.level.vip,

  darkSurfaceSolidStatusMember: DARK_PALETTE.neutral.white,
  darkSurfaceSolidStatusAmethyst: DARK_PALETTE.amethyst[400],
  darkSurfaceSolidStatusTopaz: DARK_PALETTE.topaz[400],
  darkSurfaceSolidStatusAquamarine: DARK_PALETTE.aquamarine[400],
  darkSurfaceSolidStatusOpal: DARK_PALETTE.neutral.white,
  darkSurfaceSolidStatusEmerald: DARK_PALETTE.emerald[300],
  darkSurfaceSolidStatusRuby: DARK_PALETTE.ruby[300],
  darkSurfaceSolidStatusSapphire: DARK_PALETTE.sapphire[300],
  darkSurfaceSolidStatusDiamond: DARK_PALETTE.neutral.white,
  darkSurfaceSolidStatusVip: DARK_PALETTE.neutral.white,

  accentSurfacePrimary: DARK_PALETTE.onyx[700],
  accentSurfaceSecondary: DARK_PALETTE['onyx-alpha'][500],
  accentSurfaceDisabled: DARK_PALETTE['onyx-alpha'][300],

  level_0: DARK_PALETTE.neutral.white,
  level_1: DARK_PALETTE.amethyst[400],
  level_2: DARK_PALETTE.topaz[400],
  level_3: DARK_PALETTE.aquamarine[400],
  level_4: DARK_PALETTE.neutral.white,
  level_5: DARK_PALETTE.emerald[300],
  level_6: DARK_PALETTE.ruby[300],
  level_7: DARK_PALETTE.sapphire[300],
  level_8: DARK_PALETTE.neutral.white,
  level_9: DARK_PALETTE.neutral.white,

  // Border colors
  borderPrimary: DARK_PALETTE.onyx[600],
  borderSecondary: DARK_PALETTE.onyx[400],
  borderAccent: DARK_PALETTE.gradients.global.goldA,
  borderPrimaryDisabled: DARK_PALETTE['onyx-alpha'][300],
  borderSecondaryDisabled: DARK_PALETTE['onyx-alpha'][200],
  borderAccentDisabled: DARK_PALETTE['topaz-alpha'][200],
  borderWashed: DARK_PALETTE['stone-alpha'][800],

  borderBrand: DARK_PALETTE.gradients.global.citrus,
  borderAttention: DARK_PALETTE.gradients.global.ember,
  borderWarning: DARK_PALETTE.gradients.global.sunset,
  borderActive: DARK_PALETTE.neutral.white,
  borderError: DARK_PALETTE.ruby[600],

  borderGoldSolid: DARK_PALETTE.topaz[200],

  borderLevel_0: DARK_PALETTE.neutral.white,
  borderLevel_1: DARK_PALETTE.gradients.level.amethyst,
  borderLevel_2: DARK_PALETTE.gradients.level.topaz,
  borderLevel_3: DARK_PALETTE.gradients.level.aquamarine,
  borderLevel_4: DARK_PALETTE.gradients.level.opal,
  borderLevel_5: DARK_PALETTE.gradients.level.emerald,
  borderLevel_6: DARK_PALETTE.gradients.level.ruby,
  borderLevel_7: DARK_PALETTE.gradients.level.sapphire,
  borderLevel_8: DARK_PALETTE.gradients.level.diamond,
  borderLevel_9: DARK_PALETTE.gradients.level.vip,

  // Input colors
  inputBackground: DARK_PALETTE['onyx-alpha'][800],
  inputBackgroundHover: DARK_PALETTE['onyx-alpha'][500],
  inputBackgroundDisabled: DARK_PALETTE['onyx-alpha'][300],

  // Backdrop colors
  backdropPrimary: DARK_PALETTE['onyx-alpha'][300],
  backdropSecondary: DARK_PALETTE['onyx-alpha'][500],

  // Icon colors
  iconAccentSolid: DARK_PALETTE.topaz[200],
  iconPrimary: DARK_PALETTE.onyx[600],
  iconSecondary: DARK_PALETTE.neutral.white,
  iconTertiary: DARK_PALETTE.onyx[400],
  iconGrayScale: DARK_PALETTE.gradients.global.grayScale,
  iconPrimaryDisabled: DARK_PALETTE['onyx-alpha'][300],
  iconSecondaryDisabled: DARK_PALETTE.stone[700],
  iconTertiaryDisabled: DARK_PALETTE.onyx[500],
  iconOutline: DARK_PALETTE['onyx-alpha'][800],

  // Community Jackpot
  borderEmphasis: DARK_PALETTE.gradients.global.emphasize,
  borderGraph: DARK_PALETTE.gradients.global.graph,
  borderGraphInversed: DARK_PALETTE.gradients.global.graphInversed,

  // Prize Wall Campaign - temporary solution
  // We should received these colours from backend
  borderWarmUp: DARK_PALETTE.gradients.global.warmUp,
  backdropWarmUp: '#EC784E',
  warmUpBackground: DARK_PALETTE.gradients.global.warmedUp,

  transparent: 'transparent',
};

export const LIGHT_TOKENS = DARK_TOKENS;

const SIZES: SizesMap = {
  '0': 0,
  '025': 1,
  '050': 2,
  '100': 4,
  '200': 8,
  '300': 12,
  '400': 16,
  '500': 20,
  '600': 24,
  '800': 32,
  '1000': 40,
  '1200': 48,
  '1400': 56,
  '1600': 64,
  '1800': 72,
  '2000': 80,
  '3000': 120,
  '5000': 160,
  full: 9999,
};

const SPACING: WithBreakpoints<Spacings> = {
  'space-0': {
    mobile: SIZES['0'],
    tablet: SIZES['0'],
    desktop: SIZES['0'],
  },
  'space-025': {
    mobile: SIZES['025'],
    tablet: SIZES['025'],
    desktop: SIZES['025'],
  },
  'space-050': {
    mobile: SIZES['050'],
    tablet: SIZES['050'],
    desktop: SIZES['050'],
  },
  'space-100': {
    mobile: SIZES['100'],
    tablet: SIZES['100'],
    desktop: SIZES['100'],
  },
  'space-200': {
    mobile: SIZES['200'],
    tablet: SIZES['200'],
    desktop: SIZES['200'],
  },
  'space-300': {
    mobile: SIZES['300'],
    tablet: SIZES['300'],
    desktop: SIZES['300'],
  },
  'space-400': {
    mobile: SIZES['400'],
    tablet: SIZES['400'],
    desktop: SIZES['400'],
  },
  'space-500': {
    mobile: SIZES['500'],
    tablet: SIZES['500'],
    desktop: SIZES['500'],
  },
  'space-600': {
    mobile: SIZES['600'],
    tablet: SIZES['600'],
    desktop: SIZES['600'],
  },
  'space-800': {
    mobile: SIZES['800'],
    tablet: SIZES['800'],
    desktop: SIZES['800'],
  },
  'space-1000': {
    mobile: SIZES['1000'],
    tablet: SIZES['1000'],
    desktop: SIZES['1000'],
  },
  'space-1200': {
    mobile: SIZES['1200'],
    tablet: SIZES['1200'],
    desktop: SIZES['1200'],
  },
  'space-1400': {
    mobile: SIZES['1400'],
    tablet: SIZES['1400'],
    desktop: SIZES['1400'],
  },
  'space-1600': {
    mobile: SIZES['1600'],
    tablet: SIZES['1600'],
    desktop: SIZES['1600'],
  },
  'space-1800': {
    mobile: SIZES['1800'],
    tablet: SIZES['1800'],
    desktop: SIZES['1800'],
  },
  'space-2000': {
    mobile: SIZES['2000'],
    tablet: SIZES['2000'],
    desktop: SIZES['2000'],
  },
  'space-3000': {
    mobile: SIZES['3000'],
    tablet: SIZES['3000'],
    desktop: SIZES['3000'],
  },
  'space-5000': {
    mobile: SIZES['5000'],
    tablet: SIZES['5000'],
    desktop: SIZES['5000'],
  },
  'space-full': {
    mobile: SIZES['full'],
    tablet: SIZES['full'],
    desktop: SIZES['full'],
  },
  'safe-area-inset-bottom': {
    mobile: SIZES['0'],
    tablet: SIZES['0'],
    desktop: SIZES['0'],
  },
  'max-page-width': {
    mobile: 600,
    tablet: 800,
    desktop: 1000,
  },
  '-space-0': {
    mobile: -SIZES['0'],
    tablet: -SIZES['0'],
    desktop: -SIZES['0'],
  },
  '-space-025': {
    mobile: -SIZES['025'],
    tablet: -SIZES['025'],
    desktop: -SIZES['025'],
  },
  '-space-050': {
    mobile: -SIZES['050'],
    tablet: -SIZES['050'],
    desktop: -SIZES['050'],
  },
  '-space-100': {
    mobile: -SIZES['100'],
    tablet: -SIZES['100'],
    desktop: -SIZES['100'],
  },
  '-space-200': {
    mobile: -SIZES['200'],
    tablet: -SIZES['200'],
    desktop: -SIZES['200'],
  },
  '-space-300': {
    mobile: -SIZES['300'],
    tablet: -SIZES['300'],
    desktop: -SIZES['300'],
  },
  '-space-400': {
    mobile: -SIZES['400'],
    tablet: -SIZES['400'],
    desktop: -SIZES['400'],
  },
  '-space-500': {
    mobile: -SIZES['500'],
    tablet: -SIZES['500'],
    desktop: -SIZES['500'],
  },
  '-space-600': {
    mobile: -SIZES['600'],
    tablet: -SIZES['600'],
    desktop: -SIZES['600'],
  },
  '-space-800': {
    mobile: -SIZES['800'],
    tablet: -SIZES['800'],
    desktop: -SIZES['800'],
  },
  '-space-1000': {
    mobile: -SIZES['1000'],
    tablet: -SIZES['1000'],
    desktop: -SIZES['1000'],
  },
  '-space-1200': {
    mobile: -SIZES['1200'],
    tablet: -SIZES['1200'],
    desktop: -SIZES['1200'],
  },
  '-space-1400': {
    mobile: -SIZES['1400'],
    tablet: -SIZES['1400'],
    desktop: -SIZES['1400'],
  },
  '-space-1600': {
    mobile: -SIZES['1600'],
    tablet: -SIZES['1600'],
    desktop: -SIZES['1600'],
  },
  '-space-1800': {
    mobile: -SIZES['1800'],
    tablet: -SIZES['1800'],
    desktop: -SIZES['1800'],
  },
  '-space-2000': {
    mobile: -SIZES['2000'],
    tablet: -SIZES['2000'],
    desktop: -SIZES['2000'],
  },
  '-space-3000': {
    mobile: -SIZES['3000'],
    tablet: -SIZES['3000'],
    desktop: -SIZES['3000'],
  },
  '-space-5000': {
    mobile: -SIZES['5000'],
    tablet: -SIZES['5000'],
    desktop: -SIZES['5000'],
  },
  '-space-full': {
    mobile: -SIZES['full'],
    tablet: -SIZES['full'],
    desktop: -SIZES['full'],
  },
  auto: {
    mobile: 'auto',
    tablet: 'auto',
    desktop: 'auto',
  },
  'safe-area-inset-top': {
    mobile: 0,
    tablet: 0,
    desktop: 0,
  },
  'safe-area-inset-top-pure': {
    mobile: 0,
    tablet: 0,
    desktop: 0,
  },
  'safe-area-inset-right': {
    mobile: 0,
    tablet: 0,
    desktop: 0,
  },
  'safe-area-inset-bottom-pure': {
    mobile: 0,
    tablet: 0,
    desktop: 0,
  },
  'safe-area-inset-left': {
    mobile: 0,
    tablet: 0,
    desktop: 0,
  },
};

const BORDER_RADIUSES: WithBreakpoints<BorderRadiuses> = {
  'radius-0': {
    mobile: SIZES['0'],
    tablet: SIZES['0'],
    desktop: SIZES['0'],
  },
  'radius-100': {
    mobile: SIZES['100'],
    tablet: SIZES['100'],
    desktop: SIZES['100'],
  },
  'radius-200': {
    mobile: SIZES['200'],
    tablet: SIZES['200'],
    desktop: SIZES['200'],
  },
  'radius-300': {
    mobile: SIZES['300'],
    tablet: SIZES['300'],
    desktop: SIZES['300'],
  },
  'radius-400': {
    mobile: SIZES['400'],
    tablet: SIZES['400'],
    desktop: SIZES['400'],
  },
  'radius-600': {
    mobile: SIZES['600'],
    tablet: SIZES['600'],
    desktop: SIZES['600'],
  },
  'radius-full': {
    mobile: SIZES['full'],
    tablet: SIZES['full'],
    desktop: SIZES['full'],
  },
};

const BORDER_WIDTHS: BorderWidths = {
  'border-width-0': SIZES['0'],
  'border-width-025': SIZES['025'],
  'border-width-050': SIZES['050'],
  'border-width-100': SIZES['100'],
};

const LINE_HEIGHTS: LineHeights = {
  'line-height-0': '100%',
  'line-height-025': '105%',
  'line-height-050': '125%',
  'line-height-100': '130%',
  'line-height-200': '135%',
};

const LETTER_SPACINGS: LetterSpacings = {
  'letter-spacing-0': 0,
  'letter-spacing-025': -0.5,
  'letter-spacing-050': -0.2,
  'letter-spacing-100': 3,
  'letter-spacing-200': 5,
};

const FONT_SIZES: WithBreakpoints<FontSizes> = {
  'text-0': {
    mobile: 10,
    tablet: 10,
    desktop: 10,
  },
  'text-025': {
    mobile: 11,
    tablet: 11,
    desktop: 11,
  },
  'text-050': {
    mobile: 12,
    tablet: 12,
    desktop: 12,
  },
  'text-100': {
    mobile: 14,
    tablet: 14,
    desktop: 14,
  },
  'text-200': {
    mobile: 16,
    tablet: 16,
    desktop: 16,
  },
  'text-300': {
    mobile: 18,
    tablet: 18,
    desktop: 18,
  },
  'text-400': {
    mobile: 20,
    tablet: 20,
    desktop: 20,
  },
  'text-500': {
    mobile: 24,
    tablet: 24,
    desktop: 24,
  },
  'text-600': {
    mobile: 26,
    tablet: 26,
    desktop: 26,
  },
  'text-800': {
    mobile: 32,
    tablet: 32,
    desktop: 32,
  },
  'text-1000': {
    mobile: 36,
    tablet: 36,
    desktop: 36,
  },
  'text-1200': {
    mobile: 40,
    tablet: 40,
    desktop: 40,
  },
  'text-1400': {
    mobile: 48,
    tablet: 48,
    desktop: 48,
  },
  'text-1600': {
    mobile: 56,
    tablet: 56,
    desktop: 56,
  },
  'text-1800': {
    mobile: 64,
    tablet: 64,
    desktop: 64,
  },
  'text-2000': {
    mobile: 72,
    tablet: 72,
    desktop: 72,
  },
  'text-3000': {
    mobile: 96,
    tablet: 96,
    desktop: 96,
  },
  'text-5000': {
    mobile: 124,
    tablet: 148,
    desktop: 148,
  },
};

export const COLOR_TOKENS = {
  dark: DARK_TOKENS,
  light: LIGHT_TOKENS,
};

export const DEFAULT_THEME = 'light';

export const STATIC_TOKENS: StaticTokens = {
  borderWidths: BORDER_WIDTHS,
  lineHeights: LINE_HEIGHTS,
  letterSpacings: LETTER_SPACINGS,
};

export const RESPONSIVE_TOKENS: ResponsiveTokens = {
  spacing: SPACING,
  fontSizes: FONT_SIZES,
  borderRadiuses: BORDER_RADIUSES,
};
